import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * LOS FILTROS VIVEN EN LA URL — patrón B1 del canon, y el fallo nº1 de
 * `silva-service-360`.
 *
 * Sin esto: filtras, refrescas y vuelves a empezar; y el enlace que le mandas a
 * alguien abre sin tus filtros. Con esto, además, **el agente puede contestar
 * con un enlace directo**: «mira aquí: /inventario?vista=por-acabarse».
 *
 * Tres reglas que hacen que no ensucie la barra de direcciones:
 *
 *  1. Un valor igual al de por defecto NO escribe parámetro. Una URL limpia es
 *     la que no tiene nada, no la que lleva `?estado=todas&orden=fecha`.
 *  2. Un valor que no está entre los válidos degrada al de por defecto en vez
 *     de dejar un filtro fantasma que no se ve en la pantalla pero sí filtra.
 *  3. Se usa `replace`: así «atrás» sale de la pantalla en vez de recorrer uno
 *     por uno los quince filtros que tocaste.
 */

export type ValoresDeFiltro = Record<string, string | undefined>;

export interface FiltrosEnUrl<T extends ValoresDeFiltro> {
  /** Los valores actuales, ya con los de por defecto aplicados. */
  filtros: T;
  /** Cambia uno. `undefined` o el valor por defecto lo borran de la URL. */
  poner: <K extends keyof T & string>(clave: K, valor: T[K] | undefined) => void;
  /** Cambia varios de una vez (una sola entrada en el historial). */
  ponerVarios: (cambios: Partial<T>) => void;
  /** Deja todo como al principio. */
  limpiar: () => void;
  /** Los que NO están en su valor por defecto. Alimenta los chips de «activos». */
  activos: Array<{ clave: string; valor: string }>;
  /** `true` si hay algo que limpiar. */
  hayFiltros: boolean;
}

export function useFiltrosEnUrl<T extends ValoresDeFiltro>(
  porDefecto: T,
  validos?: Partial<Record<keyof T & string, readonly string[]>>,
): FiltrosEnUrl<T> {
  const [params, setParams] = useSearchParams();

  const filtros = useMemo(() => {
    const salida = { ...porDefecto } as T;
    for (const clave of Object.keys(porDefecto) as Array<keyof T & string>) {
      const crudo = params.get(clave);
      if (crudo == null || crudo === '') continue;
      const permitidos = validos?.[clave];
      // Regla 2: un valor inválido no filtra en secreto, cae al de por defecto.
      if (permitidos && !permitidos.includes(crudo)) continue;
      salida[clave] = crudo as T[keyof T & string];
    }
    return salida;
    // `params.toString()` y no `params`: el objeto cambia de identidad en cada
    // render aunque la URL sea la misma, y eso recalcularía todo sin motivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  const escribir = useCallback(
    (cambios: Partial<T>) => {
      const siguientes = new URLSearchParams(params);
      for (const [clave, valor] of Object.entries(cambios)) {
        const defecto = porDefecto[clave as keyof T];
        // Regla 1: lo que vale lo mismo que el defecto no ocupa la URL.
        if (valor == null || valor === '' || valor === defecto) siguientes.delete(clave);
        else siguientes.set(clave, String(valor));
      }
      setParams(siguientes, { replace: true }); // Regla 3
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.toString(), setParams],
  );

  const poner = useCallback(
    <K extends keyof T & string>(clave: K, valor: T[K] | undefined) =>
      escribir({ [clave]: valor } as Partial<T>),
    [escribir],
  );

  const limpiar = useCallback(() => {
    const siguientes = new URLSearchParams(params);
    for (const clave of Object.keys(porDefecto)) siguientes.delete(clave);
    setParams(siguientes, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString(), setParams]);

  const activos = useMemo(
    () =>
      (Object.keys(porDefecto) as Array<keyof T & string>)
        .filter((k) => filtros[k] !== porDefecto[k] && filtros[k] != null && filtros[k] !== '')
        .map((k) => ({ clave: k, valor: String(filtros[k]) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtros],
  );

  return { filtros, poner, ponerVarios: escribir, limpiar, activos, hayFiltros: activos.length > 0 };
}
